<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { TcMenuItemDetail } from "@/Components/UI";

import { CurrencyUi } from "@/Common/Models/Ui/CurrencyUi";
import type { Invoice } from "@/Modules/Models/Domain/Invoice";

import { dateToDateUi } from "@/Utils/Date";
import { isValidString } from "@/Utils/String";

export interface InvoiceDetailProps {
  invoice: Invoice;
}

const props = defineProps<InvoiceDetailProps>();

const emit = defineEmits<{ (event: "selectInvoice", uuid: string): void }>();

const { t } = useI18n();

const date = computed(() => {
  const correctiveLabel =
    props.invoice.isRectified && isValidString(props.invoice.originalInvoice)
      ? ` | ${t("settings.invoices.details.tabs.corrective_of")} ${props.invoice.originalInvoice}`
      : "";

  return `${dateToDateUi(props.invoice.invoiceDate)}${correctiveLabel}`;
});

const price = computed(() => {
  const amount = props.invoice.isRectified
    ? `-${props.invoice.invoiceAmountRefunded / 100}`
    : props.invoice.invoiceAmount / 100;

  return `${amount} ${CurrencyUi[props.invoice.currency]}`;
});
</script>

<template>
  <TcMenuItemDetail
    class="invoice"
    :title="date"
    titleClass="color-content-mid typography-low-s-important"
    @click="emit('selectInvoice', props.invoice.uuid)"
  >
    <template #menu-item-description>
      <p class="invoice__description">
        {{ props.invoice.customerName }}
      </p>
    </template>

    <p class="invoice__price">
      {{ price }}
    </p>
  </TcMenuItemDetail>
</template>

<style scoped lang="scss">
.invoice {
  @include sp.padding(r, 0);

  align-items: flex-end;

  &__description {
    @include sp.margin(t, 1);
  }

  &__price {
    white-space: nowrap;
  }
}
</style>
